import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { ClipboardList, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#E24B4A', bg: '#F5F7FA' };
const when = iso => (iso ? new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const Stat = ({ k, v, tone, testid }) => (
  <div className="rounded-sm px-3 py-2" style={{ background: C.bg }} data-testid={testid}>
    <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: C.slate }}>{k}</p>
    <p className="text-sm font-mono font-semibold" style={{ color: tone || C.navy }}>{v}</p>
  </div>
);

// Family 6 · Job B — the 50-address test run: what the last run measured, and the tick that runs it again.
export function JobBCard() {
  const api = useApi();
  const { user } = useAuth();
  const canRun = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [rep, setRep] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [product, setProduct] = useState('ra-home-v1');
  const load = useCallback(() => api.get('/jobs/job-b/report').then(r => { setRep(r.data); setMissing(false); })
    .catch(e => { if (e?.response?.status === 404) setMissing(true); }), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rep?.running && rep?.status !== 'running') return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [rep?.running, rep?.status, load]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/jobs/job-b/run', { params: { product } });
      toast[data.status === 'accepted' ? 'success' : 'message'](`Job B: ${data.status}${data.stop_reason ? ` — ${data.stop_reason}` : ''}`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not run'); } finally { setBusy(false); }
  };
  if (!rep && !missing) return null;
  const s = rep?.summary, e = rep?.eval_pack, pm = rep?.proof_metric, t = s?.timings_ms?.total;
  const live = rep?.running || rep?.status === 'running';
  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" style={{ borderLeft: `3px solid ${C.blue}` }} data-testid="job-b-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" style={{ color: C.blue }} />
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Family 6 · Job B — 50-address test run</h3>
            {live && <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: C.blue }} data-testid="job-b-running"><Loader2 className="w-3 h-3 animate-spin" />running</span>}
          </div>
          <p className="text-[11px] mt-1" style={{ color: C.slate }}>
            Public addresses through the broker's journey — resolve, property card, questions with prefill, three options, drivers off the trace — timed per stage,
            typed inputs read off the record, then the explanation eval pack. On demand only; nothing is bound.
          </p>
        </div>
        {canRun && (
          <div className="flex items-center gap-2">
            <select value={product} onChange={ev => setProduct(ev.target.value)} className="text-[11px] border rounded-sm px-2 py-1 bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid="job-b-product">
              <option value="ra-home-v1">Home</option>
              <option value="ra-landlord-v1">Landlord</option>
            </select>
            <button onClick={run} disabled={busy || live} className="px-3 py-1.5 rounded-sm text-[11px] font-semibold flex items-center gap-1 disabled:opacity-50"
              style={{ background: C.blue, color: '#fff' }} data-testid="job-b-run">
              <Play className="w-3 h-3" />Run Job B
            </button>
          </div>
        )}
      </div>
      {missing && <p className="text-[11px] mt-3" style={{ color: C.slate }} data-testid="job-b-never">Job B has not run yet.</p>}
      {rep && s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-3">
            <Stat k="Attempted" v={`${s.attempted} / ${rep.fixture?.rows}`} testid="job-b-attempted" />
            <Stat k="Resolved · exact PID" v={`${s.resolved} · ${s.exact_pid}`} testid="job-b-resolved" />
            <Stat k="Quoted clean / referred" v={`${s.quoted_clean} / ${s.quoted_referred}`} testid="job-b-quoted" />
            <Stat k="Errors · rate" v={`${s.errors} · ${s.error_rate != null ? Math.round(s.error_rate * 100) : 0}%`} tone={s.errors ? C.amber : C.green} testid="job-b-errors" />
            <Stat k={`Typed p50 / max (≤ ${pm?.typed_budget})`} v={`${s.typed_inputs?.p50 ?? '—'} / ${s.typed_inputs?.max ?? '—'}`} tone={s.typed_inputs?.over_budget ? C.red : C.green} testid="job-b-typed" />
            <Stat k="Machine p50 / p95" v={t?.p50 != null ? `${(t.p50 / 1000).toFixed(1)}s / ${(t.p95 / 1000).toFixed(1)}s` : '—'} testid="job-b-timing" />
            <Stat k="Eval pack" v={e ? `${e.totals.passed}/${e.totals.cases} · ${e.invented_factors} invented` : '—'} tone={e?.passed ? C.green : C.red} testid="job-b-eval" />
          </div>
          <p className="text-[10px] mt-2 font-mono" style={{ color: C.slate }} data-testid="job-b-meta">
            run {rep.run_id} · {rep.product} · {rep.mode} · {when(rep.started_at)} · {rep.status} — {rep.stop_reason} · {rep.cost?.platform_credits_used ?? 0}/{rep.cost?.caps?.platform_credits} credits, {rep.cost?.llm_calls_on_path ?? 0} model calls
            {rep.files?.markdown ? ` · report ${rep.files.markdown}` : ''}
          </p>
          {rep.addresses?.length > 0 && (
            <details className="mt-2">
              <summary className="text-[11px] cursor-pointer font-semibold" style={{ color: C.blue }} data-testid="job-b-addresses-toggle">Addresses ({rep.addresses.length})</summary>
              <table className="w-full text-[10px] mt-2">
                <thead><tr style={{ color: C.slate }}><th className="text-left py-1">#</th><th className="text-left py-1">Address</th><th className="text-left py-1">Status</th><th className="text-right py-1">Typed</th><th className="text-right py-1">Options</th><th className="text-left py-1 pl-2">Referrals / stop</th><th className="text-right py-1">ms</th></tr></thead>
                <tbody>
                  {rep.addresses.map(a => (
                    <tr key={a.id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`job-b-address-${a.id}`}>
                      <td className="py-1 font-mono">{a.id}</td>
                      <td className="py-1" style={{ color: C.navy }}>{a.address}<span style={{ color: C.slate }}> · {a.kind}</span></td>
                      <td className="py-1 font-semibold" style={{ color: a.status === 'quoted' ? C.green : a.status === 'failed' ? C.red : C.amber }}>{a.status}</td>
                      <td className="py-1 text-right font-mono">{a.typed_inputs ?? '—'}</td>
                      <td className="py-1 text-right font-mono">{a.options ?? '—'}</td>
                      <td className="py-1 pl-2" style={{ color: C.slate }}>{a.stop || (a.referrals || []).join(', ') || a.error || '—'}</td>
                      <td className="py-1 text-right font-mono">{a.timings_ms?.total ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </Card>
  );
}
